import type { Dashboard, Metric, Unit } from '@pixerito/api-client';
import { monthLong, percentChange } from './format';
import { dashboardRange, previousRange, rangeLong } from './period';

// ── Planilha (CSV) ──────────────────────────────────────────────
// Feita para abrir direto no Excel e no Google Planilhas em português: separador ";", decimal com vírgula,
// acentos preservados (BOM UTF-8) e quebra de linha CRLF.

const SEPARATOR = ';';

const UNIT_LABEL: Record<Unit, string> = { count: 'contagem', percent: '%', rating: 'nota (0 a 5)' };

const HEADER = [
  'Cliente',
  'Tipo',
  'Módulo',
  'Grupo',
  'Métrica',
  'Unidade',
  'Período',
  'Valor',
  'Período anterior',
  'Valor anterior',
  'Variação',
  'Unidade da variação',
];

const VIDEO_HEADER = ['Vídeo', 'Plataforma', 'Publicado em', 'Visualizações', 'Curtidas', 'Comentários', 'Compartilhamentos', 'Link'];

type Cell = string | number | null | undefined;

/** Número com vírgula decimal e sem separador de milhar, para o Excel em português ler como número. */
export function csvNumber(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  return String(Math.round(value * 100) / 100).replace('.', ',');
}

/**
 * Texto de uma célula. Entre aspas se tiver ";" ou aspas. Se começar com =, +, - ou @, ganha um apóstrofo na frente:
 * o título de um vídeo não pode virar fórmula quando a planilha é aberta (injeção de fórmula em CSV).
 */
export function csvText(value: string | null | undefined) {
  if (value === null || value === undefined) return '';
  let text = value.replace(/\s*[\r\n]+\s*/g, ' ');
  if (/^[=+\-@\t]/.test(text)) text = `'${text}`;
  return /[;"]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const line = (cells: Cell[]) => cells.map((c) => (typeof c === 'number' ? csvNumber(c) : csvText(c))).join(SEPARATOR);

const deltaUnit = (metric: Pick<Metric, 'deltaKind' | 'unit'>) =>
  metric.deltaKind === 'percent' ? '%' : metric.unit === 'rating' ? 'pt' : 'p.p.';

/** "2026-08-26" → "26/08/2026" (o Excel em português reconhece como data). */
const brDate = (iso: string | null) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '');

/**
 * As análises do período que está na tela, em uma tabela só: o resumo de cada métrica contra o período anterior,
 * a evolução mês a mês das métricas com gráfico e os totais de vídeo. Os vídeos publicados vêm numa segunda tabela.
 * Devolve null se o cliente ainda não tem nenhum mês.
 */
export function buildCsv(data: Dashboard): string | null {
  const range = dashboardRange(data);
  if (!range) return null;
  const previous = previousRange(data);
  const periodText = rangeLong(range);
  const previousText = previous ? rangeLong(previous) : '';
  const client = data.client.name;

  const lines = [line(HEADER)];

  for (const m of data.modules) {
    if (m.kind === 'metrics') {
      for (const x of m.metrics) {
        lines.push(
          line([
            client,
            'Período',
            m.label,
            x.group,
            x.label,
            UNIT_LABEL[x.unit],
            periodText,
            x.current,
            x.previous === null ? '' : previousText,
            x.previous,
            x.delta,
            x.delta === null ? '' : deltaUnit(x),
          ]),
        );
      }
      if (m.series) {
        for (const x of m.metrics.filter((metric) => metric.chartable)) {
          const values = m.series.values[x.key] ?? [];
          m.series.periods.forEach((period, i) => {
            const value = values[i];
            if (value === null || value === undefined) return;
            lines.push(line([client, 'Mês', m.label, x.group, x.label, UNIT_LABEL[x.unit], monthLong(period), value, '', '', '', '']));
          });
        }
      }
    } else if (m.type === 'video' && m.videos?.isRecurring) {
      const { current, previous: before } = m.videos;
      const totals: [string, number, number | undefined][] = [
        ['Vídeos publicados', current.count, before?.count],
        ['Visualizações', current.views, before?.views],
        ['Curtidas', current.likes, before?.likes],
        ['Comentários', current.comments, before?.comments],
        ['Compartilhamentos', current.shares, before?.shares],
      ];
      for (const [label, value, earlier] of totals) {
        const change = earlier === undefined ? null : percentChange(value, earlier);
        lines.push(
          line([
            client,
            'Período',
            m.label,
            '',
            label,
            UNIT_LABEL.count,
            periodText,
            value,
            earlier === undefined ? '' : previousText,
            earlier,
            change,
            change === null ? '' : '%',
          ]),
        );
      }
    }
  }

  const videos = data.modules.find((m) => m.type === 'video')?.videos;
  if (videos?.isRecurring && videos.items.length > 0) {
    lines.push('', line(VIDEO_HEADER));
    for (const v of videos.items) {
      lines.push(line([v.title, v.platform, brDate(v.publishedOn), v.views, v.likes, v.comments, v.shares, v.url]));
    }
  }

  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

const slug = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** "pixerito-resultados-cafe-grao-dourado-2026-07-a-2026-09.csv" */
export function exportFileName(data: Dashboard, extension: string) {
  const range = dashboardRange(data);
  const period = range ? (range.from === range.to ? range.from : `${range.from}-a-${range.to}`) : 'sem-dados';
  return `pixerito-resultados-${slug(data.client.name)}-${period}.${extension}`;
}

/** Título do relatório: o navegador usa como nome do arquivo ao salvar como PDF. */
export function reportTitle(data: Dashboard) {
  const range = dashboardRange(data);
  return ['Resultados', data.client.name, range ? rangeLong(range) : ''].filter(Boolean).join(' · ');
}

// ── Entrega no navegador ────────────────────────────────────────

export function downloadFile(name: string, content: string, mime: string) {
  const url = URL.createObjectURL(new Blob([content], { type: `${mime};charset=utf-8` }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Abre a impressão (escolhendo "Salvar como PDF" sai o arquivo). O título da página vira o nome sugerido. */
export function printReport(title: string) {
  const original = document.title;
  document.title = title;
  const restore = () => {
    document.title = original;
    window.removeEventListener('afterprint', restore);
  };
  window.addEventListener('afterprint', restore);
  window.print();
}
